import { useCallback, useEffect, useState, type FC } from 'react'
import { useNavigate } from 'react-router'
import { useGetEnrollmentHYCSSettingUpData, useSubmitEnrollmentHYCSSettingUpData } from '@egw/api'
import { GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { ROUTES } from '../../../constants'
import { LoadingAnimation } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - HYCS - SettingUp')

export const SettingUpPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const [step, setStep] = useState(0)

  const { data } = useGetEnrollmentHYCSSettingUpData()
  const { mutate, error: submitError } = useSubmitEnrollmentHYCSSettingUpData()

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = useCallback(async (): Promise<void> => {
    try {
      const response = await mutate({
        proceedLoading: true,
      })
      if (response?.path) {
        void navigate(response.path)
      } else {
        // TODO: Handle the case when path is not returned
        // void navigate(ROUTES.HOME.path);
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }, [mutate, navigate])

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prevStep) => {
        if (prevStep < 2) {
          return prevStep + 1
        }

        clearInterval(interval)
        void onSubmit()
        return prevStep
      })
    }, 2000)

    return () => {
      clearInterval(interval)
    }
  }, [onSubmit])

  return (
    <EnrollmentHYCSLayout title={t('hycs.setting_up.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-center">
        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!submitError ? (
          <div className="flex flex-col justify-center items-center flex-1">
            <LoadingAnimation />
            <p className="typo-primary-xl-light mt-md">{t(`investments.loading.step${step.toString()}`)}</p>
          </div>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {submitError ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
